/**
 * RunCalendarPage — NC-1269 Slice 1 (Jahreskalender).
 *
 * A tutor asked for a year calendar of all trainings. Slice 1 is deliberately
 * read-only and needs no API change: the runs list already carries every field
 * the approved criteria ask for (period, room, workload, participant count,
 * cost center) — verified field by field in the issue before this was cut.
 *
 * The feature that makes this more than a re-skinned list is the **double
 * booking marker**: two runs in the same room whose periods overlap are called
 * out, in the year overview and in the month table. Surfacing that is the whole
 * reason a calendar beats a sorted table.
 *
 * All date shaping lives in utils/runCalendar so the rules (NC-457 field
 * precedence, date-only end-of-day stretch, year-boundary clipping) are testable
 * without a DOM, and so this file stays a rendering concern.
 *
 * NOT in this slice (both blocked on an operator interpretation, see the issue):
 * "Raumvorbereitung" (no such field exists anywhere) and "Hardwareverfuegbarkeit"
 * (ambiguous between room.capacity and Azure vCPU quota).
 */

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { runsApi } from '../services/api';
import { roomsApi } from '../services/roomsService';
import { workloadsApi } from '../services/workloadsService';
import {
  PaginatedResponse,
  Run,
  SchulungsraumDefinition,
  WorkloadDefinition,
} from '../services/types';
import { formatDateSafe } from '../utils/dateFormat';
import { buildCalendarYear, availableYears, CalendarEntry } from '../utils/runCalendar';
import { useApiQuery } from '../hooks/useApiQuery';
import { TableSkeleton } from '../components/SkeletonLoaders';
import EmptyState from '../components/EmptyState';
import ErrorMessage from '../components/ErrorMessage';

const MONTH_NAMES = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

/**
 * A calendar must show finished trainings too — a year view that hides last
 * spring's courses answers the wrong question. Hence `view: 'all'` (NC-1077)
 * instead of the list page's 'active' default.
 *
 * The page size is generous but finite; `hasMore` is surfaced rather than
 * swallowed, because a silently truncated calendar reads as "no more trainings".
 */
const CALENDAR_PAGE_SIZE = 500;

export default function RunCalendarPage() {
  const navigate = useNavigate();
  const [year, setYear] = useState<number>(() => new Date().getFullYear());
  const [mode, setMode] = useState<'year' | 'month'>('year');
  const [month, setMonth] = useState<number>(() => new Date().getMonth());

  const { data, loading, error, refetch } = useApiQuery<PaginatedResponse<Run>>(
    () => runsApi.list({ view: 'all', pageSize: CALENDAR_PAGE_SIZE }),
    [],
  );
  const { data: rooms } = useApiQuery<SchulungsraumDefinition[]>(() => roomsApi.list(), []);
  const { data: workloads } = useApiQuery<WorkloadDefinition[]>(() => workloadsApi.list(), []);

  // Memoised because it feeds the useMemo deps below: `data?.items ?? []` would
  // hand out a fresh array identity on every render and re-shape the whole
  // calendar each time (and CI turns that exhaustive-deps warning into an error).
  const runs = useMemo(() => data?.items ?? [], [data]);

  const roomById = useMemo(() => {
    const map = new Map<string, SchulungsraumDefinition>();
    for (const room of rooms ?? []) map.set(room.id, room);
    return map;
  }, [rooms]);

  const workloadById = useMemo(() => {
    const map = new Map<string, WorkloadDefinition>();
    for (const workload of workloads ?? []) map.set(workload.id, workload);
    return map;
  }, [workloads]);

  const years = useMemo(() => availableYears(runs, new Date().getFullYear()), [runs]);
  const calendar = useMemo(() => buildCalendarYear(runs, year), [runs, year]);

  const conflictCount = calendar.entries.filter((e) => e.conflictsWith.length > 0).length;

  const roomLabel = (run: Run): string => {
    if (!run.roomId) return 'kein Raum';
    const room = roomById.get(run.roomId);
    if (!room) return run.roomId;
    return room.location ? `${room.name} (${room.location})` : room.name;
  };

  const workloadLabel = (run: Run): string => {
    const id = run.workloadId ?? run.workload;
    const def = id ? workloadById.get(id) : undefined;
    return def?.name ?? (id ? id.toUpperCase() : '—');
  };

  const periodLabel = (entry: CalendarEntry): string =>
    `${formatDateSafe(entry.start)} – ${formatDateSafe(entry.end)}`;

  const openRun = (runId: string) => {
    if (runId) navigate(`/runs/${encodeURIComponent(runId)}`);
  };

  const conflictTooltip = (entry: CalendarEntry): string =>
    `Doppelbelegung: überschneidet sich im selben Raum mit ${entry.conflictsWith.join(', ')}`;

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Schulungskalender
        </Typography>
        <ErrorMessage message={error} onRetry={refetch} />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Schulungskalender</Typography>
        <Button startIcon={<RefreshIcon />} onClick={refetch} disabled={loading}>
          Aktualisieren
        </Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Jahr"
                value={String(year)}
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {years.map((y) => (
                  <MenuItem key={y} value={String(y)}>
                    {y}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4} md={3}>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={mode}
                onChange={(_e: unknown, next: 'year' | 'month' | null) => next && setMode(next)}
                aria-label="Ansicht"
              >
                <ToggleButton value="year" aria-label="Jahresansicht">
                  Jahr
                </ToggleButton>
                <ToggleButton value="month" aria-label="Monatsansicht">
                  Monat
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            {mode === 'month' && (
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Monat"
                  value={String(month)}
                  onChange={(e) => setMonth(Number(e.target.value))}
                >
                  {MONTH_NAMES.map((name, index) => (
                    <MenuItem key={name} value={String(index)}>
                      {name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {conflictCount > 0 && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
          {conflictCount === 1
            ? '1 Lauf überschneidet sich mit einem anderen Lauf im selben Raum.'
            : `${conflictCount} Läufe überschneiden sich mit anderen Läufen im selben Raum.`}{' '}
          Betroffene Einträge sind markiert.
        </Alert>
      )}

      {data?.hasMore && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Es wurden die ersten {CALENDAR_PAGE_SIZE} Läufe geladen — der Kalender ist
          möglicherweise nicht vollständig.
        </Alert>
      )}

      {calendar.unplaceable.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {calendar.unplaceable.length}{' '}
          {calendar.unplaceable.length === 1 ? 'Lauf hat' : 'Läufe haben'} kein verwertbares
          Datum und {calendar.unplaceable.length === 1 ? 'ist' : 'sind'} im Kalender nicht
          darstellbar.
        </Alert>
      )}

      {loading && runs.length === 0 ? (
        <Card>
          <TableSkeleton
            rows={6}
            columns={6}
            headers={['Zeitraum', 'Kurs', 'Workload', 'Raum', 'Teilnehmer', 'Kostenstelle']}
          />
        </Card>
      ) : calendar.entries.length === 0 ? (
        <EmptyState
          variant="no-results"
          title={`Keine Schulungen in ${year}`}
          description={
            'Für dieses Jahr sind keine Läufe hinterlegt. Über die Jahresauswahl lassen sich ' +
            'andere Jahre prüfen — ein Lauf über den Jahreswechsel erscheint in beiden Jahren.'
          }
        />
      ) : mode === 'year' ? (
        <Grid container spacing={2}>
          {calendar.months.map((bucket) => (
            <Grid item xs={12} sm={6} md={4} key={bucket.month}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  {/* The month hook sits on a Box, not on the Card: the MUI test
                      mock's Card stub drops unknown props, so a `data-testid`
                      there would exist in prod and be invisible to every test —
                      an assertion that can only ever pass. */}
                  <Box data-testid={`calendar-month-${bucket.month}`}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle1">{MONTH_NAMES[bucket.month]}</Typography>
                      <Chip
                        size="small"
                        label={bucket.entries.length}
                        color={bucket.entries.length > 0 ? 'primary' : 'default'}
                      />
                    </Stack>

                    {bucket.entries.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        keine Schulungen
                      </Typography>
                    ) : (
                      <Stack spacing={1}>
                        {bucket.entries.map((entry) => (
                          <Box
                            key={`${bucket.month}-${entry.runId}`}
                            sx={{ cursor: 'pointer' }}
                            onClick={() => openRun(entry.runId)}
                          >
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2">{entry.run.course || entry.runId}</Typography>
                              {entry.conflictsWith.length > 0 && (
                                <Tooltip title={conflictTooltip(entry)}>
                                  <Chip
                                    size="small"
                                    color="warning"
                                    label="Doppelbelegung"
                                    data-testid={`conflict-${entry.runId}`}
                                  />
                                </Tooltip>
                              )}
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                              {periodLabel(entry)} · {roomLabel(entry.run)} ·{' '}
                              {entry.run.accounts?.length ?? 0} TN
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              {MONTH_NAMES[month]} {year}
            </Typography>
            {calendar.months[month].entries.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                keine Schulungen in diesem Monat
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Zeitraum</TableCell>
                      <TableCell>Kurs</TableCell>
                      <TableCell>Workload</TableCell>
                      <TableCell>Raum</TableCell>
                      <TableCell align="center">Teilnehmer</TableCell>
                      <TableCell>Kostenstelle</TableCell>
                      <TableCell align="right">Aktion</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {calendar.months[month].entries.map((entry) => (
                      <TableRow key={entry.runId} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <span>{periodLabel(entry)}</span>
                            {entry.conflictsWith.length > 0 && (
                              <Tooltip title={conflictTooltip(entry)}>
                                <Chip
                                  size="small"
                                  color="warning"
                                  label="Doppelbelegung"
                                  data-testid={`conflict-row-${entry.runId}`}
                                />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>{entry.run.course || '—'}</TableCell>
                        <TableCell>{workloadLabel(entry.run)}</TableCell>
                        <TableCell>{roomLabel(entry.run)}</TableCell>
                        <TableCell align="center">{entry.run.accounts?.length ?? 0}</TableCell>
                        <TableCell>{entry.run.costCenter || '—'}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            startIcon={<ViewIcon />}
                            onClick={() => openRun(entry.runId)}
                            aria-label={`Lauf ${entry.runId} öffnen`}
                          >
                            Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
